import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../lib/axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { trackAddToCart } from '../lib/trackEvent';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch cart when user logs in (only for customers, not admins)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Only fetch cart for customers, not admin/vendor
      if (user.role === 'customer') {
        fetchCart();
      }
    } else {
      // Load cart from localStorage for guest users
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        setCart(JSON.parse(guestCart));
      }
    }
  }, [isAuthenticated, user]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated && user && user.role === 'customer') {
        // Fetch from API for authenticated users
        const response = await axiosInstance.get(`${API_URL}/api/cart`);
        setCart(response.data);
      } else {
        // Load from localStorage for guest users
        const guestCart = localStorage.getItem('guestCart');
        if (guestCart) {
          setCart(JSON.parse(guestCart));
        } else {
          setCart({ items: [], total: 0 });
        }
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      // Don't show error notification in admin panel or for auth errors
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      if (error.response?.status !== 401 && !isAdminRoute) {
        toast.error('Failed to load cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (product, quantity = 1) => {
    try {
      setLoading(true);
      const productId = product?.id || product?._id || product;
      
      if (isAuthenticated && user && user.role === 'customer') {
        const response = await axiosInstance.post(`${API_URL}/api/cart/add`, null, {
          params: { product_id: productId, quantity }
        });
        
        // Update cart with response data
        if (response.data && response.data.cart) {
          setCart(response.data.cart);
        } else {
          // Fallback: fetch cart
          await fetchCart();
        }
        toast.success('Item added to cart!');
        trackAddToCart(undefined, productId);
      } else {
        // Handle guest cart
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items": [], "total": 0}');
        
        // Get product details if only ID passed
        let productData = product;
        if (typeof product === 'string') {
          const productResponse = await axiosInstance.get(`${API_URL}/api/products/${productId}`);
          productData = productResponse.data;
        }
        
        const existingItem = guestCart.items.find(item => item.product_id === productId);
        
        if (existingItem) {
          existingItem.quantity += quantity;
        } else {
          guestCart.items.push({
            product_id: productId,
            quantity,
            price: productData.sale_price || productData.price,
            name: productData.name,
            image: productData.images?.[0] || null
          });
        }
        
        guestCart.total = guestCart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        localStorage.setItem('guestCart', JSON.stringify(guestCart));
        setCart(guestCart);
        toast.success('Item added to cart!');
        trackAddToCart(productData.sale_price || productData.price, productId);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Failed to add item to cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      
      if (isAuthenticated && user && user.role === 'customer') {
        // API call to remove from server cart
        const response = await axiosInstance.delete(`${API_URL}/api/cart/remove/${productId}`);
        
        // Update cart with response data
        if (response.data && response.data.cart) {
          setCart(response.data.cart);
        } else {
          // Fallback: fetch cart
          await fetchCart();
        }
      } else {
        // Handle guest cart
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items": [], "total": 0}');
        guestCart.items = guestCart.items.filter(item => item.product_id !== productId);
        guestCart.total = guestCart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        localStorage.setItem('guestCart', JSON.stringify(guestCart));
        setCart(guestCart);
      }
      
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to remove item from cart');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      return removeFromCart(productId);
    }
    
    try {
      setLoading(true);
      
      if (isAuthenticated && user && user.role === 'customer') {
        // API call to update quantity
        const response = await axiosInstance.put(`${API_URL}/api/cart/update`, null, {
          params: {
            product_id: productId,
            quantity: quantity
          }
        });
        
        // Update cart with response data
        if (response.data && response.data.cart) {
          setCart(response.data.cart);
        } else {
          // Fallback: fetch cart
          await fetchCart();
        }
        toast.success('Quantity updated');
      } else {
        // Handle guest cart
        const guestCart = JSON.parse(localStorage.getItem('guestCart') || '{"items": [], "total": 0}');
        const item = guestCart.items.find(item => item.product_id === productId);
        
        if (item) {
          item.quantity = quantity;
          guestCart.total = guestCart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          localStorage.setItem('guestCart', JSON.stringify(guestCart));
          setCart(guestCart);
          toast.success('Quantity updated');
        }
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      toast.error('Failed to update quantity');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated) {
        await axiosInstance.delete(`${API_URL}/api/cart/clear`);
        setCart({ items: [], total: 0 });
      } else {
        localStorage.removeItem('guestCart');
        setCart({ items: [], total: 0 });
      }
      
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Failed to clear cart:', error);
      // Don't show error for clearing cart
    } finally {
      setLoading(false);
    }
  };

  const getCartItemsCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  const mergeGuestCart = async () => {
    const guestCart = localStorage.getItem('guestCart');
    if (guestCart && isAuthenticated) {
      const guestCartData = JSON.parse(guestCart);
      
      try {
        // Add all guest cart items to server cart
        for (const item of guestCartData.items) {
          await addToCart(item.product_id, item.quantity);
        }
        
        // Clear guest cart
        localStorage.removeItem('guestCart');
      } catch (error) {
        console.error('Failed to merge guest cart:', error);
      }
    }
  };

  // Merge guest cart when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      mergeGuestCart();
    }
  }, [isAuthenticated, user]);

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemsCount,
    fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
